import { z } from "zod"

/**
 * When:
 * - Sent on page focus, which vendor may not receive at all.
 * - Sent indeterminately.
 */
const GetCallStateEventSchema = z.object({
  /**
   * The message action type
   */
  type: z.literal("amn:parent/getCallState"),
})

export type GetCallStateEvent = z.infer<typeof GetCallStateEventSchema>

/**
 * The parent may request the local media state at any time. The child should immediately reply
 * with amn:child/localMediaState.
 */
const GetLocalMediaStateEventSchema = z.object({
  /*
   * The message action type
   */
  type: z.literal("amn:parent/getLocalMediaState"),

  payload: z.object({
    /*
     * The media type.
     */
    mediaType: z.enum(["audio", "video"]),
  }),
})

export type GetLocalMediaStateEvent = z.infer<
  typeof GetLocalMediaStateEventSchema
>

/**
 * Sent by the parent to enable or disable the local audio or video stream.
 *
 * When:
 * - An interpreter uses the parent interface to toggle their local audio and/or video.
 * - The parent receives its first amn:child/callState.
 */
const SetLocalMediaEventSchema = z.object({
  /**
   * The message action type
   */
  type: z.literal("amn:parent/setLocalMedia"),

  payload: z.object({
    /**
     * The media type.
     */
    mediaType: z.enum(["video", "audio"]),

    /**
     * Whether to enable or disable (mute) the users local media.
     */
    enabled: z.boolean(),
  }),
})

export type SetLocalMediaEvent = z.infer<typeof SetLocalMediaEventSchema>

/**
 * Sent by the parent to update the volume of all remote participants video/audio tracks.
 *
 * When:
 * - An interpreter uses a range control to adjust the video volume.
 * - Can be sent before the call session or video is established.
 */
const SetVolumeEventSchema = z.object({
  /**
   * The message action type
   */
  type: z.literal("amn:parent/setVolume"),

  payload: z.object({
    /**
     * The requested volume as a double from 0 through 1.
     * 0 is no audio and 1 is max volume.
     */
    volume: z.number().min(0).max(1),
  }),
})

export type SetVolumeEvent = z.infer<typeof SetVolumeEventSchema>

/**
 * Sent by the parent to restart the media or reconnect the call.
 *
 * When:
 * - The parent wants to refresh the call session.
 */
const RefreshCallEventSchema = z.object({
  /**
   * The message action type
   */
  type: z.literal("amn:parent/refreshCall"),
})

export const AmnIncomingEventSchema = z.union([
  GetCallStateEventSchema,
  SetLocalMediaEventSchema,
  SetVolumeEventSchema,
  RefreshCallEventSchema,
  GetLocalMediaStateEventSchema,
])

export type AmnIncomingEvent = z.infer<typeof AmnIncomingEventSchema>
